import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faCreditCard,
  faExchangeAlt,
  faChartLine,
  faShieldAlt,
  faCode,
  faDatabase,
  faGlobe,
  faBell,
  faServer,
  faUserShield,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';

const Features = () => {
  const features = [
    {
      icon: faWallet,
      title: "Multi-Chain Support",
      description: "Connect and process payments across all major blockchains including Ethereum, Polygon, Solana, and Binance Smart Chain.",
      category: "Integration"
    },
    {
      icon: faShieldAlt,
      title: "Smart Contract Escrow",
      description: "Secure transaction processing with audited smart contracts that ensure safe and reliable payment flows.",
      category: "Security"
    },
    {
      icon: faChartLine,
      title: "Real-Time Analytics",
      description: "Comprehensive dashboard with transaction metrics, conversion rates, and business insights.",
      category: "Analytics"
    },
    {
      icon: faExchangeAlt,
      title: "Instant Conversion",
      description: "Real-time crypto-to-fiat conversion with competitive rates and minimal slippage.",
      category: "Exchange"
    },
    {
      icon: faServer,
      title: "Scalable Infrastructure",
      description: "Enterprise-grade infrastructure that scales with your business needs and transaction volumes.",
      category: "Technology"
    },
    {
      icon: faCode,
      title: "Developer APIs",
      description: "Comprehensive API suite with SDKs for major programming languages and platforms.",
      category: "Development"
    },
    {
      icon: faDatabase,
      title: "Multi-Currency Support",
      description: "Support for all major cryptocurrencies and fiat currencies with automatic conversion.",
      category: "Payments"
    },
    {
      icon: faGlobe,
      title: "Global Compliance",
      description: "Built-in compliance with international regulations and KYC/AML requirements.",
      category: "Compliance"
    },
    {
      icon: faUserShield,
      title: "Fraud Protection",
      description: "Advanced fraud detection and prevention systems to protect your business and customers.",
      category: "Security"
    },
    {
      icon: faCreditCard,
      title: "Multiple Payout Options",
      description: "Receive funds via bank transfer, credit card, or keep a portion in cryptocurrency.",
      category: "Payments"
    },
    {
      icon: faBell,
      title: "Automated Notifications",
      description: "Customizable alerts and notifications for all transaction activities.",
      category: "User Experience"
    },
    {
      icon: faFileContract,
      title: "Smart Invoicing",
      description: "Generate and manage invoices with automatic payment reconciliation.",
      category: "Business"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        {/* Animated blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-green-500">
              Powerful Payment Features
            </h1>
            <p className="text-xl text-gray-600">
              Explore our comprehensive suite of Web3-to-Web2 payment bridge features designed for businesses and developers
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white p-8 rounded-xl border border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:bg-blue-200 transition-all duration-300">
                  <FontAwesomeIcon icon={feature.icon} className="text-blue-500 text-2xl" />
                </div>
                <span className="text-sm text-blue-500 mb-2 block">{feature.category}</span>
                <h3 className="text-xl font-bold mb-4 text-gray-800 group-hover:text-blue-500 transition-all duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-green-500">
              Why Choose UniPay?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Compare our solution with traditional payment processors and see the difference
            </p>
          </motion.div>

          <div className="overflow-x-auto rounded-xl shadow-lg">
            <table className="w-full min-w-[800px] border-collapse bg-white">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-4 text-left text-gray-700 font-semibold border-b">Feature</th>
                  <th className="p-4 text-center bg-blue-50 text-blue-700 font-semibold border-b">UniPay</th>
                  <th className="p-4 text-center text-gray-700 font-semibold border-b">Traditional Payment Processors</th>
                  <th className="p-4 text-center text-gray-700 font-semibold border-b">Crypto Payment Gateways</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Accept Crypto Payments</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✗</td>
                  <td className="p-4 text-center text-gray-500">✓</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Fiat Settlement</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✓</td>
                  <td className="p-4 text-center text-gray-500">Limited</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Multi-Chain Support</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✗</td>
                  <td className="p-4 text-center text-gray-500">Limited</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Real-Time Conversion</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✗</td>
                  <td className="p-4 text-center text-gray-500">Limited</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Low Transaction Fees</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✗</td>
                  <td className="p-4 text-center text-gray-500">✓</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 text-gray-800">Developer APIs</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">Limited</td>
                  <td className="p-4 text-center text-gray-500">✓</td>
                </tr>
                <tr>
                  <td className="p-4 text-gray-800">Regulatory Compliance</td>
                  <td className="p-4 text-center bg-blue-50 text-blue-500 font-medium">✓</td>
                  <td className="p-4 text-center text-gray-500">✓</td>
                  <td className="p-4 text-center text-gray-500">Limited</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/2 right-1/2 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-green-500">
                Ready to Transform Your Payment Experience?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join thousands of businesses already using UniPay to bridge Web3 and Web2 payments
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-200 transition-all duration-300"
                >
                  Get Started
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-500 border border-blue-200 px-8 py-3 rounded-lg font-semibold hover:border-blue-500 hover:shadow-lg hover:shadow-blue-100 transition-all duration-300"
                >
                  Contact Sales
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
